/**
 * Date formatting utilities
 * Provides consistent date formatting across the application
 */

/**
 * Formats a date string to Brazilian date format (DD/MM/YYYY)
 * @param dateString - ISO date string or Date-parseable string
 * @returns Formatted date string or '-' if invalid
 * @example
 * formatDate('2024-03-15') // Returns '15/03/2024'
 * formatDate(null) // Returns '-'
 */
export const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) return '-';

    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '-';
  }
};

/**
 * Formats a date string to Brazilian date and time format (DD/MM/YYYY HH:MM)
 * @param dateString - ISO date string or Date-parseable string
 * @returns Formatted date and time string or '-' if invalid
 * @example
 * formatDateTime('2024-03-15T14:30:00') // Returns '15/03/2024 14:30'
 * formatDateTime(null) // Returns '-'
 */
export const formatDateTime = (dateString?: string | null): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) return '-';

    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
};

/**
 * Formats a date string to Brazilian date with time and seconds (DD/MM/YYYY HH:MM:SS)
 * @param dateString - ISO date string or Date-parseable string
 * @returns Formatted date, time and seconds string or '-' if invalid
 * @example
 * formatDateTimeSeconds('2024-03-15T14:30:45') // Returns '15/03/2024 14:30:45'
 */
export const formatDateTimeSeconds = (dateString?: string | null): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) return '-';

    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return '-';
  }
};

/**
 * Formats a date string to long Brazilian format (e.g., "15 de março de 2024")
 * @param dateString - ISO date string or Date-parseable string
 * @returns Long formatted date string or '-' if invalid
 * @example
 * formatDateLong('2024-03-15') // Returns '15 de março de 2024'
 */
export const formatDateLong = (dateString?: string | null): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) return '-';

    return date.toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '-';
  }
};

/**
 * Formats a date string to short Brazilian format with abbreviated month (e.g., "15/mar/2024")
 * @param dateString - ISO date string or Date-parseable string
 * @returns Short formatted date string or '-' if invalid
 * @example
 * formatDateShort('2024-03-15') // Returns '15/mar/2024'
 */
export const formatDateShort = (dateString?: string | null): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) return '-';

    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '-';
  }
};
